import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { useApolloClient } from '@apollo/client';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import { generateSequence } from '~/modules/resourcing/common/util';
import { patchResourceRequest } from '~/modules/projects/resourcing-plan/enhancers/patchResourceRequest';
import {
  useUpdateResourceRequest,
  useToggleRemoveRequestDialog
} from '~/modules/resourcing/common/hooks';
import { useGetResourceRequestFilter } from '~/modules/resourcing/common/hooks/useGetResourceRequestFilter';
import useRequestPeriodEditorChangeHandlers from '../hooks/useRequestPeriodEditorChangeHandlers';
import useResourceRequestDragIndicators from '../hooks/useResourceRequestDragIndicators';
import ResourceRequestPeriodEditPopover from '../ResourceRequestPeriodEditPopover/ResourceRequestPeriodEditPopover';
import ResourceRequestOverlayPeriodBlock from '../ResourceRequestOverlayPeriodBlock/ResourceRequestOverlayPeriodBlock';
import useGetDraftRequestPeriods from '../hooks/useGetDraftRequestPeriods';
import ResourceRequestPeriodDeleteDialog from './ResourceRequestPeriodDeleteDialog';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(1)
  }
}));

export const ResourceRequestDrafts = ({
  scale,
  chartDisplayDateRange,
  chartDates,
  isEditable,
  resourceRequest,
  isResourceActualModeEnabled,
  isPSAActualCornerCasesEnabled,
  isPSARMP2023Q3BugsEnabled
}) => {
  const {
    showRemoveRequestDialog,
    openRemoveRequestDialog,
    closeRemoveRequestDialog
  } = useToggleRemoveRequestDialog();

  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const classes = useStyles();

  const [requestPeriodEditTarget, setRequestPeriodEditTarget] = useState(null);
  const apolloClient = useApolloClient();
  const { updateResourceRequest } = useUpdateResourceRequest();
  const { project } = useProjectContext();
  const filter = useGetResourceRequestFilter();
  const [{ start: chartStartDate }] = chartDates;
  const onPeriodClose = useCallback(
    ({ request }) => {
      patchResourceRequest({
        openRemoveRequestDialog,
        updateResourceRequest,
        project,
        request,
        apolloClient,
        filter,
        isPSAActualCornerCasesEnabled,
        isResourceActualModeEnabled
      });
    },
    [
      project,
      updateResourceRequest,
      openRemoveRequestDialog,
      apolloClient,
      filter,
      isPSAActualCornerCasesEnabled,
      isResourceActualModeEnabled
    ]
  );

  const onPeriodClick = useCallback(req => setSelectedRequestId(req.id), [
    setSelectedRequestId
  ]);

  const {
    handleRequestPeriodClick,
    handleRequestPeriodClose,
    setPreviousPeriod,
    setNextPeriod
  } = useRequestPeriodEditorChangeHandlers({
    onPeriodClose,
    onPeriodClick,
    requestPeriodEditTarget,
    setRequestPeriodEditTarget
  });

  const dragState = useResourceRequestDragIndicators({
    chartStartDate,
    scale,
    resourceRequest,
    project,
    isResourceActualModeEnabled,
    isPSAActualCornerCasesEnabled
  });

  const { requestPeriods } = useGetDraftRequestPeriods({
    chartDates,
    chartStartDate,
    scale,
    resourceRequest,
    projectDefaultScheduleRule: project.defaultScheduleRule
  });

  const allRequestPeriods = index =>
    requestPeriods.map(
      ({
        key,
        startDate,
        endDate,
        totalHours,
        totalCost,
        isOverlay,
        isPartialPeriod,
        totalScheduleHours,
        dynamicPosition,
        percentage
      }) => (
        <ResourceRequestOverlayPeriodBlock
          isPSARMP2023Q3BugsEnabled={isPSARMP2023Q3BugsEnabled}
          key={key}
          index={index}
          startDate={startDate}
          endDate={endDate}
          isPartialPeriod={isPartialPeriod}
          isOverlay={isOverlay}
          totalHours={totalHours}
          scale={scale}
          chartDisplayDateRange={chartDisplayDateRange}
          handleRequestPeriodClick={handleRequestPeriodClick}
          totalCost={totalCost}
          percentageValue={percentage}
          requestPeriods={requestPeriods}
          totalScheduleHours={totalScheduleHours}
          isEditable={isEditable}
          resourceRequest={resourceRequest}
          dynamicPosition={dynamicPosition}
          dragState={dragState}
        />
      )
    );

  return (
    <div className={`${classes.root} resourceRequestDraft`}>
      {generateSequence(resourceRequest.quantity).map((request, rowIndex) =>
        allRequestPeriods(rowIndex)
      )}
      {requestPeriodEditTarget && requestPeriodEditTarget.anchorEl && (
        <ResourceRequestPeriodEditPopover
          scale={scale}
          id="period-editor"
          anchorEl={requestPeriodEditTarget.anchorEl}
          resourceRequest={resourceRequest}
          periodDetails={requestPeriodEditTarget}
          project={project}
          allocatedHours={requestPeriodEditTarget.totalHours}
          requestedHours={requestPeriodEditTarget.totalScheduleHours}
          handleRequestPeriodClose={handleRequestPeriodClose}
          setNextPeriod={setNextPeriod}
          setPreviousPeriod={setPreviousPeriod}
        />
      )}
      {showRemoveRequestDialog &&
        selectedRequestId &&
        resourceRequest.id === selectedRequestId && (
          <ResourceRequestPeriodDeleteDialog
            resourceRequest={resourceRequest}
            showRemoveRequestDialog={showRemoveRequestDialog}
            closeRemoveRequestDialog={closeRemoveRequestDialog}
            isPSAActualCornerCasesEnabled={isPSAActualCornerCasesEnabled}
            isResourceActualModeEnabled={isResourceActualModeEnabled}
          />
        )}
    </div>
  );
};

ResourceRequestDrafts.propTypes = {
  scale: PropTypes.string.isRequired,
  resourceRequest: PropTypes.object.isRequired,
  isEditable: PropTypes.bool.isRequired,
  chartDates: PropTypes.array,
  chartDisplayDateRange: PropTypes.object.isRequired,
  isResourceActualModeEnabled: PropTypes.bool,
  isPSAActualCornerCasesEnabled: PropTypes.bool,
  isPSARMP2023Q3BugsEnabled: PropTypes.bool
};

export default ResourceRequestDrafts;
